import { useEffect, useMemo, useRef, useState } from 'react';
import {
  RESIDENT_PIN_MAX_ATTEMPTS,
  RESIDENT_PIN_RESEND_COOLDOWN_SECONDS,
  type PublicCommunity,
} from '@tsr/shared-types';
import * as V from './residentSubscribeVocabulary';
import {
  fetchPublicCommunities,
  requestResidentPin,
  confirmResidentPin,
  unsubscribeResident,
} from './publicSubscribeApi';

/**
 * #547 — the PUBLIC resident /subscribe page. A STANDALONE surface (main.tsx mounts it with
 * the AuthGate bypassed) — NO COP chrome, NO Cesium. Self-contained light layout: a resident
 * picks their community, enters a mobile number, confirms a texted 6-digit PIN, and is
 * subscribed to their community's emergency text updates. All copy comes from
 * residentSubscribeVocabulary (lock-covered); all network via publicSubscribeApi (no operator auth).
 *
 * The "tries left" counter and the resend cooldown are CLIENT-driven — the confirm endpoint
 * returns a constant { confirmed } (no attempts/locked flag) so it leaks no enumeration oracle.
 */

type Step = 'community' | 'phone' | 'pin' | 'success' | 'unsubscribe' | 'unsubscribed';

export function SubscribeView(): JSX.Element {
  const search = new URLSearchParams(window.location.search);
  const unsubToken = search.get('token');
  const startUnsub = unsubToken != null || search.get('unsubscribe') === '1';

  const [step, setStep] = useState<Step>(startUnsub ? 'unsubscribe' : 'community');
  const [communities, setCommunities] = useState<PublicCommunity[]>([]);
  const [loadingCommunities, setLoadingCommunities] = useState(true);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<PublicCommunity | null>(null);
  const [phone, setPhone] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [pin, setPin] = useState('');
  const [attemptsUsed, setAttemptsUsed] = useState(0);
  const [resendIn, setResendIn] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resendTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load the pickable community catalog once (catalog only — never the roster).
  useEffect(() => {
    const ctrl = new AbortController();
    fetchPublicCommunities(ctrl.signal)
      .then((c) => setCommunities(c))
      .catch(() => setCommunities([]))
      .finally(() => setLoadingCommunities(false));
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    return () => {
      if (resendTimer.current) clearInterval(resendTimer.current);
    };
  }, []);

  function startResendCooldown(seconds: number): void {
    if (resendTimer.current) clearInterval(resendTimer.current);
    setResendIn(seconds);
    resendTimer.current = setInterval(() => {
      setResendIn((s) => {
        if (s <= 1 && resendTimer.current) clearInterval(resendTimer.current);
        return Math.max(0, s - 1);
      });
    }, 1000);
  }

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') return communities.slice(0, 8);
    return communities.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 8);
  }, [communities, query]);

  const triesLeft = Math.max(0, RESIDENT_PIN_MAX_ATTEMPTS - attemptsUsed);
  const locked = triesLeft <= 0;

  async function sendPin(community: PublicCommunity): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const res = await requestResidentPin(community.id, phone);
      if (!res.ok) {
        setError(res.error === 'invalid_phone' ? V.ERR_INVALID_PHONE : V.ERR_GENERIC);
        return;
      }
      setMaskedPhone(res.phone_masked ?? '');
      setAttemptsUsed(0);
      setPin('');
      startResendCooldown(res.resend_cooldown_seconds ?? RESIDENT_PIN_RESEND_COOLDOWN_SECONDS);
      setStep('pin');
    } catch {
      setError(V.ERR_GENERIC);
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitPhone(): Promise<void> {
    if (!picked) return;
    await sendPin(picked);
  }

  async function onResend(): Promise<void> {
    if (!picked || resendIn > 0) return;
    await sendPin(picked);
  }

  async function onConfirm(): Promise<void> {
    if (!picked || locked) return;
    setBusy(true);
    setError(null);
    try {
      const res = await confirmResidentPin(picked.id, phone, pin.trim());
      if (res.confirmed) {
        setStep('success');
        return;
      }
      // Constant { confirmed:false } from the server → client tracks the attempt count.
      const used = attemptsUsed + 1;
      setAttemptsUsed(used);
      setError(used >= RESIDENT_PIN_MAX_ATTEMPTS ? V.ERR_LOCKED : V.ERR_WRONG_CODE);
      setPin('');
    } catch {
      setError(V.ERR_GENERIC);
    } finally {
      setBusy(false);
    }
  }

  async function onUnsubscribe(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if (unsubToken) {
        await unsubscribeResident({ unsubscribe_token: unsubToken });
        setStep('unsubscribed');
        return;
      }
      if (!picked) {
        setError(V.ERR_GENERIC);
        return;
      }
      await unsubscribeResident({ community_id: picked.id, phone });
      setStep('unsubscribed');
    } catch {
      setError(V.ERR_GENERIC);
    } finally {
      setBusy(false);
    }
  }

  const inputCls =
    'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-base text-slate-900 placeholder:text-slate-400 focus:border-sky-600 focus:outline-none focus:ring-2 focus:ring-sky-200';
  const primaryBtn =
    'w-full rounded-lg bg-sky-700 px-4 py-2.5 text-base font-semibold text-white hover:bg-sky-800 disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 text-slate-900" data-testid="subscribe-view">
      <div className="mx-auto w-full max-w-md">
        <header className="mb-5">
          <h1 className="text-2xl font-bold tracking-tight" data-testid="subscribe-title">
            {V.PAGE_TITLE}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="subscribe-trust-line">
            {V.TRUST_LINE}
          </p>
        </header>

        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          {/* Step 1 — community */}
          {step === 'community' && (
            <div data-testid="step-community">
              <h2 className="mb-3 text-base font-semibold">{V.COMMUNITY_STEP_HEADING}</h2>
              <input
                type="search"
                data-testid="community-search"
                className={inputCls}
                placeholder={V.COMMUNITY_SEARCH_PLACEHOLDER}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <div className="mt-3 space-y-1.5" data-testid="community-results">
                {loadingCommunities ? (
                  <p className="py-4 text-center text-sm text-slate-500">{V.COMMUNITY_LOADING}</p>
                ) : matches.length === 0 ? (
                  <p className="py-4 text-center text-sm text-slate-500" data-testid="community-empty">
                    {V.COMMUNITY_EMPTY_STATE}
                  </p>
                ) : (
                  matches.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      data-testid="community-option"
                      onClick={() => {
                        setPicked(c);
                        setError(null);
                        setStep('phone');
                      }}
                      className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5 text-left hover:border-sky-500 hover:bg-sky-50"
                    >
                      <span className="font-medium">{c.name}</span>
                      <span className="text-xs text-slate-500">{V.communityTypeLabel(c.community_type)}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Step 2 — phone */}
          {step === 'phone' && picked && (
            <div data-testid="step-phone">
              <h2 className="mb-1 text-base font-semibold">{V.PHONE_STEP_HEADING}</h2>
              <p className="mb-3 text-sm text-slate-600" data-testid="phone-community-framing">
                {V.willTextFor(picked.name)}
              </p>
              <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="resident-phone">
                {V.PHONE_LABEL}
              </label>
              <input
                id="resident-phone"
                type="tel"
                inputMode="tel"
                data-testid="phone-input"
                className={inputCls}
                placeholder={V.PHONE_PLACEHOLDER}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <p className="mt-2 text-xs text-slate-500">{V.CONSENT_NOTE}</p>
              {error && (
                <p className="mt-2 text-sm text-red-600" data-testid="phone-error">
                  {error}
                </p>
              )}
              <button
                type="button"
                data-testid="phone-submit"
                disabled={busy || phone.trim() === ''}
                onClick={onSubmitPhone}
                className={`${primaryBtn} mt-4`}
              >
                {busy ? V.PHONE_SUBMIT_BUSY : V.PHONE_SUBMIT}
              </button>
              <button
                type="button"
                onClick={() => setStep('community')}
                className="mt-2 w-full rounded-lg px-4 py-2 text-sm text-slate-500 hover:text-slate-700"
              >
                {V.CHANGE_COMMUNITY}
              </button>
            </div>
          )}

          {/* Step 3 — pin */}
          {step === 'pin' && picked && (
            <div data-testid="step-pin">
              <h2 className="mb-1 text-base font-semibold">{V.PIN_STEP_HEADING}</h2>
              <p className="mb-1 text-sm text-slate-600" data-testid="pin-sent-to">
                {V.pinSentTo(maskedPhone)}
              </p>
              <p className="mb-3 text-xs text-slate-500">{V.PIN_EXPIRY_NOTE}</p>
              <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="resident-pin">
                {V.PIN_LABEL}
              </label>
              <input
                id="resident-pin"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                data-testid="pin-input"
                className={`${inputCls} tracking-[0.5em]`}
                placeholder={V.PIN_PLACEHOLDER}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={locked}
              />
              {!locked && (
                <p className="mt-2 text-xs text-slate-500" data-testid="pin-tries-left">
                  {V.pinTriesLeft(triesLeft)}
                </p>
              )}
              {error && (
                <p className="mt-2 text-sm text-red-600" data-testid="pin-error">
                  {error}
                </p>
              )}
              <button
                type="button"
                data-testid="pin-submit"
                disabled={busy || locked || pin.trim().length !== 6}
                onClick={onConfirm}
                className={`${primaryBtn} mt-4`}
              >
                {busy ? V.PIN_SUBMIT_BUSY : V.PIN_SUBMIT}
              </button>
              <button
                type="button"
                data-testid="pin-resend"
                disabled={busy || resendIn > 0}
                onClick={onResend}
                className="mt-2 w-full rounded-lg px-4 py-2 text-sm text-sky-700 hover:text-sky-900 disabled:text-slate-400"
              >
                {resendIn > 0 ? V.pinResendIn(resendIn) : V.PIN_RESEND}
              </button>
            </div>
          )}

          {/* Step 4 — success */}
          {step === 'success' && picked && (
            <div data-testid="step-success">
              <h2 className="mb-2 text-lg font-semibold text-emerald-700">{V.SUCCESS_HEADLINE}</h2>
              <p className="text-sm leading-relaxed text-slate-700" data-testid="success-body">
                {V.successBody(picked.name, maskedPhone)}
              </p>
              <button
                type="button"
                data-testid="success-unsubscribe"
                onClick={() => {
                  setError(null);
                  setStep('unsubscribe');
                }}
                className="mt-4 text-sm text-slate-500 underline hover:text-slate-700"
              >
                {V.SUCCESS_UNSUBSCRIBE_LINK}
              </button>
            </div>
          )}

          {/* Unsubscribe */}
          {step === 'unsubscribe' && (
            <div data-testid="step-unsubscribe">
              <h2 className="mb-3 text-base font-semibold">{V.UNSUBSCRIBE_HEADING}</h2>
              {!unsubToken && (
                <>
                  {!picked && (
                    <p className="mb-3 text-sm text-slate-600">{V.UNSUBSCRIBE_PICK_PROMPT}</p>
                  )}
                  {picked && (
                    <p className="mb-2 text-sm text-slate-600">{picked.name}</p>
                  )}
                  <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="unsub-phone">
                    {V.PHONE_LABEL}
                  </label>
                  <input
                    id="unsub-phone"
                    type="tel"
                    inputMode="tel"
                    data-testid="unsub-phone-input"
                    className={inputCls}
                    placeholder={V.PHONE_PLACEHOLDER}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </>
              )}
              {unsubToken && (
                <p className="mb-3 text-sm text-slate-600">{V.UNSUBSCRIBE_TOKEN_PROMPT}</p>
              )}
              {error && (
                <p className="mt-2 text-sm text-red-600" data-testid="unsub-error">
                  {error}
                </p>
              )}
              <button
                type="button"
                data-testid="unsub-submit"
                disabled={busy || (!unsubToken && (phone.trim() === '' || !picked))}
                onClick={onUnsubscribe}
                className={`${primaryBtn} mt-4`}
              >
                {busy ? V.UNSUBSCRIBE_SUBMIT_BUSY : V.UNSUBSCRIBE_SUBMIT}
              </button>
            </div>
          )}

          {/* Unsubscribed */}
          {step === 'unsubscribed' && (
            <div data-testid="step-unsubscribed">
              <h2 className="mb-2 text-lg font-semibold">
                {picked ? V.unsubscribeConfirmed(picked.name) : V.UNSUBSCRIBE_CONFIRMED_GENERIC}
              </h2>
            </div>
          )}
        </div>

        <footer className="mt-6 text-center text-xs text-slate-400" data-testid="subscribe-footer">
          {V.FOOTER_NOTE}
        </footer>
      </div>
    </div>
  );
}
